import { useEffect, useState } from "react";
import { Box, Stack, Typography, Tabs, Tab } from "@mui/material";
import { Helmet } from "react-helmet-async";
import ReportsTable from "./components/ReportsTable";
import { HomeWrapper } from "./ReportsDashboard.styles";
import { useLazyGetReportDataQuery } from "src/services/restApi/queries/report.service";
import useRememberTable from "src/hooks/useRememberTable";
import { ReportFilter } from "./types";
import LoadingTable from "./components/LoadingTable";

export enum ReportTypes {
  Trades = "trades",
  Deposits = "deposits",
  Withdrawals = "withdrawals",
}

const ReportsDashboardPage = () => {
  const [reportType, setReportType] = useState<ReportTypes | undefined>(
    ReportTypes.Trades
  );
  const tableName = `Pulse ${reportType} Reports`;
  const { paginationModel, sortModel, filter } = useRememberTable<ReportFilter>(
    { tableName }
  );
  const [getReportData, { data, isLoading, isFetching }] =
    useLazyGetReportDataQuery({});
  const [limit, setLimit] = useState<number>(100);

  useEffect(() => {
    if (!reportType) return;
    // List of fields that should NOT be sent as filters
    const NON_FILTER_FIELDS = [
      "limit",
      "offset",
      "orderBy",
      "orderDirection",
      "groupBy",
      "timeGroup",
      "sortField",
      "sortOrder",
    ];
    if (filter && Object.keys(filter).length > 0) {
      const formattedFilters = Object.entries(filter)
        .filter(([field]) => !NON_FILTER_FIELDS.includes(field))
        .map(([field, value]) => {
          // Handle date fields
          if (field === "fromDate" || field === "toDate") {
            return {
              field: "createdAt",
              operator: field === "fromDate" ? ">=" : "<=",
              value: value as string,
            };
          }
          // Handle country field
          if (field === "country") {
            return {
              field: "country",
              operator: "=",
              value: value as string,
            };
          }
          // Handle other fields
          return {
            field,
            operator: "=",
            value: value as string,
          };
        });

      getReportData({
        table: reportType as any,
        filters: formattedFilters,
        orderBy: sortModel[0]?.field || "createdAt",
        orderDirection: sortModel[0]?.sort || "desc",
        limit,
        offset: 0,
      });
    } else {
      getReportData({
        table: reportType as any,
        filters: [],
        orderBy: sortModel[0]?.field || "createdAt",
        orderDirection: sortModel[0]?.sort || "desc",
        limit,
        offset: 0,
      });
    }
  }, [getReportData, paginationModel, sortModel, filter, limit]);

  return (
    <HomeWrapper>
      <Helmet>
        <title>Reports</title>
      </Helmet>
      <Box mx={5}>
        <Stack spacing={3} direction="column">
          <Typography variant="h2" gutterBottom>
            Reports
          </Typography>
          <Tabs
            value={reportType}
            onChange={async (
              _event: React.SyntheticEvent,
              value: ReportTypes
            ) => {
              setReportType(undefined); // Reset report type to trigger loading state
              await getReportData({
                table: value as any,
                filters: [],
                orderBy: sortModel[0]?.field || "createdAt",
                orderDirection: "desc",
                limit,
                offset: 0,
              });

              setReportType(value);
            }}
            textColor="secondary"
            indicatorColor="secondary"
          >
            <Tab label="Trades" value={ReportTypes.Trades} />
            <Tab label="Deposits" value={ReportTypes.Deposits} />
            <Tab label="Withdrawals" value={ReportTypes.Withdrawals} />
          </Tabs>
          {!reportType ? (
            <LoadingTable />
          ) : (
            <ReportsTable
              reportType={reportType}
              data={data}
              isLoading={isLoading}
              isFetching={isFetching}
              limit={limit}
              setLimit={setLimit}
            />
          )}
        </Stack>
      </Box>
    </HomeWrapper>
  );
};

export default ReportsDashboardPage;
